<template>
  <div class="login-card">
    <!-- Step 1: Email / Password -->
    <template v-if="!formData.token">
      <div class="login-card__inner">
        <div class="login-card__top">
          <img
            src="../../assets/logo/ochpower.svg"
            alt="OCH POWER"
            class="login-card__logo"
          >
          <SelectLanguageInput
            real-time-mode
            outlined
            class="login-card__language-select"
          />
        </div>

        <!-- TODO: 2026/10/05頃までインフォメーションを掲載予定         -->
        <v-alert
          v-if="showRenewalNotice"
          type="info"
          variant="tonal"
          density="compact"
          closable
          class="login-card__alert login-card__notice"
          @click:close="dismissRenewalNotice"
        >
          <div class="login-card__notice-title">
            {{ $t('Login.RenewalNoticeTitle') }}
          </div>
          <div class="login-card__notice-body">
            {{ $t('Login.RenewalNoticeBody') }}
          </div>
          <div class="login-card__notice-link-row">
            <a
              class="login-card__notice-link"
              :href="renewalNoticeUrl"
              target="_blank"
            >
              {{ $t('Login.RenewalNoticeLink') }}
              <v-icon
                icon="mdi-open-in-new"
                size="14"
                class="login-card__notice-link-icon"
              />
            </a>
          </div>
        </v-alert>

        <div class="login-card__hero">
          <h1 class="login-card__title">
            {{ $t('Login.PageTitle') }}
          </h1>
          <p class="login-card__subtitle">
            {{ $t('Login.PageSubtitle') }}
          </p>
        </div>

        <v-alert
          v-if="loginError"
          type="error"
          variant="tonal"
          density="compact"
          :text="loginError"
          class="login-card__alert"
          closable
          @click:close="loginError = ''"
        />

        <v-alert
          v-else-if="errorRes.detail"
          type="error"
          variant="tonal"
          density="compact"
          :text="errorRes.detail"
          class="login-card__alert"
        />

        <div class="login-field">
          <label class="login-field__label">{{ $t('Login.EmailLabel') }}</label>
          <TextInput
            v-model="formData.username"
            field-key="username"
            :label="''"
            :placeholder="$t('Login.EmailPlaceholder')"
            icon="mdi-email-outline"
            type="email"
            class="login-field__input"
            @update:model-value="clearLoginError()"
          />
        </div>

        <div class="login-field">
          <label class="login-field__label">{{ $t('Login.PasswordLabel') }}</label>
          <PasswordInput
            v-model="formData.password"
            field-key="password"
            :label="''"
            :placeholder="$t('Login.PasswordPlaceholder')"
            icon="mdi-lock-outline"
            autocomplete="on"
            class="login-field__input"
            @keyup-enter="doLoginStep1()"
            @update:model-value="clearLoginError()"
          />
        </div>

        <div class="login-card__forgot">
          <ForgotPasswordDialog link />
        </div>

        <v-btn
          block
          class="login-card__submit"
          :loading="ui.loading"
          @click="doLoginStep1()"
        >
          {{ $t('User.Login') }}
        </v-btn>

        <p class="login-card__signup-prompt">
          {{ $t('Login.NoAccount') }}
          <PreSignupDialog link />
        </p>

        <div class="login-card__divider">
          <span>{{ $t('Login.OrContinueWith') }}</span>
        </div>

        <div class="login-card__social">
          <GoogleLogin
            :callback="onSignIn"
            outline
            block
          />
        </div>

        <div class="login-card__legal mt-3">
          <p class="login-card__legal-text">
            {{ $t('Login.TermsAgreement') }}
          </p>
          <div class="login-card__legal-links">
            <v-card-actions class="login-card__legal-actions">
              <TermsLink class="login-card__legal-link" color="secondary" />
            </v-card-actions>
          </div>
        </div>
      </div>
    </template>

    <!-- Step 2: One-time code -->
    <template v-else>
      <div class="login-card__inner">
        <div class="login-card__header-left login-card__header-left--solo">
          <img
            src="../../assets/logo/ochpower.svg"
            alt="OCH POWER"
            class="login-card__logo"
          >
        </div>

        <div class="login-card__header">
          <div class="login-card__icon">
            <v-icon
              icon="mdi-shield-key-outline"
              size="28"
              color="white"
            />
          </div>
          <h1 class="login-card__title">
            {{ $t('User.OneTimeCode') }}
          </h1>
          <p class="login-card__subtitle">
            {{ $t('Login.EnterOneTimeCode') }}
          </p>
        </div>

        <v-alert
          v-if="loginError"
          type="error"
          variant="tonal"
          density="compact"
          :text="loginError"
          class="login-card__alert"
          closable
          @click:close="loginError = ''"
        />

        <v-alert
          v-else-if="errorRes.detail"
          type="error"
          variant="tonal"
          density="compact"
          :text="errorRes.detail"
          class="login-card__alert"
        />

        <div class="login-field">
          <label class="login-field__label">{{ $t('User.OneTimeCode') }}</label>
          <TextInput
            v-model="formData.one_time_code"
            field-key="one_time_code"
            :label="''"
            :placeholder="$t('Login.OneTimeCodePlaceholder')"
            class="login-field__input"
            @keyup-enter="doLoginStep2()"
          />
        </div>

        <div class="login-card__otp-actions">
          <v-btn
            variant="outlined"
            class="login-card__otp-cancel"
            @click="cancelLoginStep2()"
          >
            {{ $t('Cancel') }}
          </v-btn>
          <v-btn
            class="login-card__submit login-card__submit--half"
            :loading="ui.loading"
            @click="doLoginStep2()"
          >
            {{ $t('Submit') }}
          </v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import http from '../../library/no_session_http'
import settings from '../../settings'
import { toLoginSuccess } from '../../library/redirect'
import { mapGetters } from 'vuex'
import SelectLanguageInput from '../../components/parts/inputs/SelectLanguageInput'
import PreSignupDialog from '../../components/dialogs/PreSignupDialog'
import TextInput from '../../components/parts/inputs/TextInput'
import PasswordInput from '../../components/parts/inputs/PasswordInput'
import TermsLink from '../../terms/TermsLink'
import ForgotPasswordDialog from '../../components/dialogs/ForgotPasswordDialog'
import GoogleLogin from '../base/GoogleLogin.vue'
import axios from 'axios'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  components: {
    ForgotPasswordDialog,
    TermsLink,
    PasswordInput,
    TextInput,
    PreSignupDialog,
    SelectLanguageInput,
    GoogleLogin
  },
  data: () => ({
    formData: {
      username: null,
      password: null,
      one_time_code: null,
      token: null
    },
    loginError: '',
    showRenewalNotice: true,
    renewalNoticeUrl: 'https://repli.support.ochpower.com/hc/ja/articles/60516696526361-Repli-Web-%E9%96%A2%E9%80%A3-2026-08-03-%E6%9B%B4%E6%96%B0'
  }),
  computed: {
    ...mapGetters(['errorRes', 'ui'])
  },
  created () {
    this.$i18n.locale = this.getBrowserLanguage()
  },
  methods: {
    dismissRenewalNotice () {
      this.showRenewalNotice = false
    },
    getBrowserLanguage () {
      let language = (window.navigator.languages && window.navigator.languages[0]) ||
        window.navigator.language ||
        window.navigator.userLanguage ||
        window.navigator.browserLanguage
      if (language.indexOf('-')) {
        language = language.split('-')[0]
      }
      return language
    },
    clearLoginError () {
      this.loginError = ''
      this.$store.dispatch('clearErrorRes')
    },
    validateStep1 () {
      const errors = {}
      if (!this.formData.username || !EMAIL_PATTERN.test(this.formData.username)) {
        errors.username = this.$t('Login.InvalidEmail')
      }
      if (!this.formData.password || this.formData.password.length < 8) {
        errors.password = this.$t('Login.InvalidPassword')
      }
      if (Object.keys(errors).length) {
        this.$store.dispatch('errorRes', errors)
        return false
      }
      return true
    },
    validateStep2 () {
      const errors = {}
      if (!this.formData.one_time_code) {
        errors.one_time_code = this.$t('Login.InvalidOneTimeCode')
      }
      if (Object.keys(errors).length) {
        this.$store.dispatch('errorRes', errors)
        return false
      }
      return true
    },
    successStep1 (res) {
      // 認証ステップ1成功、ワンタイムコード入力
      this.formData.token = res.data.token
    },
    successStep2 (res) {
      // 認証ステップ2成功、tokenをLocalStorageに保存してリダイレクト
      localStorage.setItem('token', res.data.token)
      localStorage.setItem('refresh', res.data.refresh)
      localStorage.setItem('session_id', res.data.session_id)
      toLoginSuccess()
    },
    doLoginStep1 () {
      // ユーザー名、パスワード認証
      this.loginError = ''
      this.$store.dispatch('clearErrorRes')
      if (!this.validateStep1()) return

      http.post(settings.ApiUrl + 'token-auth-s1/', {
        email: this.formData.username,
        password: this.formData.password
      }).then(
        (res) => {
          this.successStep1(res)
        }
      ).catch(error => {
        const data = error.response?.data || {}
        if (data.email || data.password) {
          this.$store.dispatch('errorRes', data)
        }
        this.loginError = data.non_field_errors
          ? data.non_field_errors[0]
          : 'An error occurred'
      })
    },
    doLoginStep2 () {
      // ワンタイムコード認証
      this.loginError = ''
      this.$store.dispatch('clearErrorRes')
      if (!this.validateStep2()) return

      const requestData = {
        token: this.formData.token,
        one_time_code: this.formData.one_time_code
      }
      http.post(settings.ApiUrl + 'token-auth-s2/', { ...requestData }, { withCredentials: true }).then(
        (res) => {
          this.successStep2(res)
        }
      ).catch(error => {
        const data = error.response?.data || {}
        if (data.one_time_code) {
          this.$store.dispatch('errorRes', data)
        }
        this.loginError = data.non_field_errors
          ? (data.non_field_errors[0] === 'Invalid code.'
            ? this.$t('Login.InvalidOneTimeCode1')
            : data.non_field_errors[0])
          : 'An error occurred'
      })
    },
    cancelLoginStep2 () {
      // キャンセル ワンタイムコード認証
      this.loginError = ''
      this.$store.dispatch('clearErrorRes')
      this.formData.token = null
      this.formData.one_time_code = null
    },
    onSignIn (googleUser) {
      axios.post(settings.ApiUrl + 'verify-google/', {
        token: googleUser.credential
      }, { withCredentials: true }).then((res) => {
        this.$store.dispatch('clearErrorRes')
        this.$store.dispatch('ui', { loading: false })
        this.successStep2(res)
      }).catch((error) => {
        this.$store.dispatch('ui', { loading: false })
        this.$store.dispatch('alert', {
          title: this.$t('SocialAccounts.loginWithGoogleFailureTitle'),
          body: this.$t('SocialAccounts.loginWithGoogleFailureContent')
        })
      })
    }
  }
}
</script>

<style scoped>
.login-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 520px;
  height: auto;
  max-height: calc(100dvh - clamp(32px, 6vh, 48px));
  padding: clamp(12px, 3vh, 28px) clamp(20px, 4vw, 40px);
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  --login-control-height: clamp(40px, 5vh, 44px);
}

.login-card__inner {
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.5vh, 16px);
}

.login-card__top {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0;
}

.login-card__logo {
  width: clamp(100px, 14vh, 140px);
  height: auto;
}

.login-card__language-select {
  flex-shrink: 0;
  width: 120px;
  max-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-card__language-select :deep(.v-input) {
  width: 120px;
  max-width: 120px;
}

.login-card__language-select :deep(.v-field) {
  width: 120px;
  max-width: 120px;
  font-size: clamp(11px, 1.4vh, 13px);
  overflow: visible;
}

.login-card__language-select :deep(.v-field__outline) {
  --v-field-border-opacity: 1;
  color: #e0e0e0;
}

.login-card__language-select :deep(.v-field__outline__start) {
  border-start-start-radius: 9999px;
  border-end-start-radius: 9999px;
}

.login-card__language-select :deep(.v-field__outline__end) {
  border-start-end-radius: 9999px;
  border-end-end-radius: 9999px;
}

.login-card__language-select :deep(.v-field__input) {
  min-height: clamp(30px, 4.5vh, 36px);
  padding-top: 0;
  padding-bottom: 0;
  padding-inline: 4px;
}

.login-card__language-select :deep(.v-field__prepend-inner),
.login-card__language-select :deep(.v-field__append-inner) {
  padding-top: 0;
  padding-bottom: 0;
}

.login-card__hero {
  flex-shrink: 0;
  margin: 0;
}

.login-card__title {
  margin: 0 0 clamp(2px, 0.6vh, 8px);
  color: #1a1a1a;
  font-size: clamp(18px, 3.2vh, 26px);
  font-weight: 700;
  line-height: 1.2;
}

.login-card__subtitle {
  margin: 0;
  color: #666;
  font-size: clamp(11px, 1.6vh, 14px);
  line-height: 1.4;
}

.login-card__signup-prompt {
  flex-shrink: 0;
  margin: 0;
  color: #666;
  font-size: clamp(11px, 1.6vh, 14px);
  text-align: center;
  line-height: 1.4;
}

.login-card__signup-prompt :deep(.login-link) {
  margin-left: 4px;
  font-weight: 600;
}

.login-card__legal {
  flex-shrink: 0;
  margin: 0;
  text-align: center;
}

.login-card__legal-text {
  margin: 0 0 clamp(2px, 0.6vh, 6px);
  color: #999;
  font-size: clamp(10px, 1.4vh, 12px);
  line-height: 1.4;
}

.login-card__legal-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.login-card__legal-actions {
  padding: 0 !important;
  min-height: 0;
}

.login-card__legal-actions :deep(.v-btn) {
  padding: 0 !important;
  min-height: 0 !important;
  height: auto !important;
}

.login-card__legal-link {
  background: none;
  border: none;
  padding: 0;
  color: #29A9CF;
  font-size: clamp(10px, 1.4vh, 12px);
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
}

.login-card__legal-link:hover {
  text-decoration: underline;
}

.login-card__header-left--solo {
  margin-bottom: clamp(12px, 2vh, 20px);
}

.login-card__header {
  text-align: center;
  margin-bottom: clamp(12px, 2vh, 24px);
}

.login-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: clamp(44px, 7vh, 56px);
  height: clamp(44px, 7vh, 56px);
  margin-bottom: clamp(8px, 1.5vh, 16px);
  background: #29A9CF;
  border-radius: 14px;
}

.login-card__alert {
  flex-shrink: 0;
  margin: 0;
}

.login-card__notice {
  border: 1px solid rgba(41, 169, 207, 0.35);
  border-radius: 8px;
}

.login-card__notice-title {
  font-size: clamp(12px, 1.6vh, 14px);
  font-weight: 700;
  line-height: 1.4;
}

.login-card__notice-body {
  margin-top: 2px;
  font-size: clamp(11px, 1.4vh, 12px);
  font-weight: 400;
  line-height: 1.5;
}

.login-card__notice-link-row {
  display: flex;
  justify-content: flex-start;
  margin-top: 6px;
  color: rgb(var(--v-theme-primary)) !important;
}

.login-card__notice-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: rgb(var(--v-theme-primary)) !important;
  font-size: clamp(11px, 1.4vh, 12px);
  font-weight: 600;
  line-height: 1.4;
  text-decoration: none;
}

.login-card__notice-link:hover {
  text-decoration: underline;
}

.login-card__notice-link-icon {
  flex-shrink: 0;
  color: #0747A6 !important;
  opacity: 1 !important;
}

.login-field {
  flex-shrink: 0;
  margin: 0;
}

.login-field__label {
  display: block;
  margin-bottom: clamp(2px, 0.5vh, 6px);
  color: #333;
  font-size: clamp(11px, 1.5vh, 13px);
  font-weight: 600;
}

.login-field__input :deep(.v-field) {
  border-radius: 8px;
  font-size: clamp(12px, 1.6vh, 14px);
}

.login-field__input :deep(.v-field__input) {
  min-height: var(--login-control-height);
  padding-top: clamp(4px, 0.8vh, 8px);
  padding-bottom: clamp(4px, 0.8vh, 8px);
}

.login-field__input :deep(.v-input__details) {
  min-height: 0;
  padding-top: 2px;
}

.login-field__input :deep(.v-field__outline) {
  --v-field-border-opacity: 1;
  color: #e0e0e0;
}

.login-field__input :deep(.v-field--focused .v-field__outline) {
  color: #29A9CF;
}

.login-field__input :deep(.v-field--error .v-field__outline) {
  color: #f44336;
}

.login-card__forgot {
  display: flex;
  flex-shrink: 0;
  justify-content: flex-start;
  margin: 0;
}

.login-card__forgot :deep(.login-link) {
  display: inline-flex;
  align-items: center;
  min-height: clamp(20px, 3vh, 28px);
  padding: 0;
  font-size: clamp(11px, 1.5vh, 13px);
  font-weight: 500;
  line-height: 1.4;
}

.login-card__submit {
  flex: 0 0 auto !important;
  align-self: stretch;
  height: var(--login-control-height) !important;
  min-height: var(--login-control-height) !important;
  max-height: var(--login-control-height) !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  background: #29A9CF !important;
  color: #fff !important;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-radius: 8px !important;
  text-transform: none;
}

.login-card__divider {
  position: relative;
  flex-shrink: 0;
  margin: 0;
  text-align: center;
}

.login-card__divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e0e0e0;
}

.login-card__divider span {
  position: relative;
  padding: 0 12px;
  background: #fff;
  color: #999;
  font-size: clamp(11px, 1.5vh, 13px);
}

.login-card__social {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  width: 100%;
}

.login-card__otp-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.login-card__otp-cancel {
  flex: 0 0 auto;
  width: auto;
  min-width: 0;
  height: var(--login-control-height) !important;
  min-height: var(--login-control-height) !important;
  padding: 0 16px !important;
  border-color: #e0e0e0 !important;
  color: #666 !important;
  text-transform: none;
  border-radius: 8px !important;
}

.login-card__submit--half {
  flex: 0 0 auto;
  min-width: 100px;
  padding: 0 24px !important;
}

@media (max-width: 520px) {
  .login-card {
    max-height: calc(100dvh - 32px);
    border-radius: 12px;
  }
}

@media (max-height: 750px) {
  .login-card__inner {
    gap: clamp(6px, 1vh, 12px);
  }

  .login-card__legal-text {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
}

@media (max-height: 650px) {
  .login-card {
    padding: clamp(10px, 1.5vh, 16px) clamp(16px, 4vw, 24px);
  }

  .login-card__logo {
    width: clamp(90px, 12vh, 110px);
  }

  .login-card__subtitle {
    -webkit-line-clamp: 2;
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
  }
}
</style>
